import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { initials } from '@/lib/format'
import { useInView } from '@/hooks/use-in-view'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/generals/page-hero'
import { CtaPanel } from '@/components/generals/cta-panel'
import { Reveal } from '@/components/generals/reveal'
import { ButtonLink } from '@/components/generals/button'
import { RollingNumber } from '@/components/generals/rolling-number'
import { SectionHeading } from '@/components/generals/section-heading'
import { FreightTechFeature } from '@/components/company/freight-tech-feature'
import { MilestoneTimeline } from '@/components/company/milestone-timeline'

import {
    companyStats,
    partnerTypes,
    team,
    values
} from '@/constants/company'

const StatBand: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { once: true, threshold: 0.35 })

    return (
        <section id='by-the-numbers' aria-labelledby='by-the-numbers-title' className='section-y'>
            <div className='shell'>
                <SectionHeading
                    id='by-the-numbers-title'
                    eyebrow="By the numbers"
                    title="Small team,"
                    accent="long reach."
                />

                <div ref={ref} className='mt-12 lg:mt-16'>
                    <Reveal as='dl' className='grid gap-4 sm:grid-cols-2 lg:grid-cols-4'>
                        {companyStats.map((stat, index) => (
                            <div
                                key={stat.label}
                                className={cn('flex flex-col justify-between rounded-[28px] p-6 sm:min-h-[200px] sm:p-7', index === 0 ? 'bg-ultramarine text-white' : 'surface text-ink')}
                            >
                                <dt className='sr-only'>
                                    {stat.label}
                                </dt>

                                <dd>
                                    <span className='headline block text-[clamp(3rem,5vw,4.25rem)] leading-none'>
                                        <RollingNumber value={stat.value} active={inView} />
                                    </span>

                                    <span aria-hidden='true' className={cn('mt-5 block text-[15px] leading-snug', index === 0 ? 'text-white/75' : 'text-ink-soft')}>
                                        {stat.label}
                                    </span>
                                </dd>
                            </div>
                        ))}
                    </Reveal>
                </div>
            </div>
        </section>
    )
}

const Company: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title="Company"
                description="Quayside is a small team in Rotterdam, Chicago and Singapore keeping big freight moving, with an integration platform and a 24/7 human operations desk."
            />

            <PageHero
                label="Company"
                titleTop="A small team keeping"
                titleBottom="big freight moving."
                description="Quayside started in a port operations office in Rotterdam. Today 64 people run flows for shippers on five continents, around the clock."
            />

            <section id='story' aria-labelledby='story-title' className='section-b'>
                <div className='shell grid gap-12 lg:grid-cols-12 lg:gap-16'>
                    <Reveal className='lg:col-span-5'>
                        <p className='eyebrow flex items-center gap-2.5 text-stone'>
                            <span aria-hidden='true' className='size-1.5 rounded-full bg-ultramarine' />
                            Our story
                        </p>

                        <h2 id='story-title' className='headline mt-5 text-[clamp(2rem,3.6vw,3rem)] text-ink [text-wrap:balance]'>
                            The hard part was never the connection.
                            <span className='block text-ultramarine'>
                                It was the exception at 2 a.m.
                            </span>
                        </h2>
                    </Reveal>

                    <Reveal delay={120} className='space-y-5 text-lg leading-relaxed text-ink-soft lg:col-span-7 lg:pt-10'>
                        <p>
                            In 2019, Elin Vos was running inbound operations for a retailer in Rotterdam. Her team worked with twelve carriers, three customs brokers, two warehouses and one ERP, and none of them talked to each other. Most days went on copying the same container number between screens.
                        </p>

                        <p>
                            Joris de Haan, an engineer who had built port community systems, offered to connect them. The first flow took a weekend. Keeping it running took a team, because the hard part was never the connection. It was the exception in the middle of the night.
                        </p>

                        <p>
                            That is still how we work. We build the connections, and we run them with people who know freight, so the exceptions are handled before they become delays.
                        </p>
                    </Reveal>
                </div>
            </section>

            <FreightTechFeature />
            <MilestoneTimeline />

            <section id='values' aria-labelledby='values-title' className='section-y'>
                <div className='shell'>
                    <SectionHeading
                        id='values-title'
                        eyebrow="Values"
                        title="How we work"
                        accent="when it matters."
                    />

                    <ul className='mt-12 grid gap-4 sm:grid-cols-2 lg:mt-16 lg:grid-cols-3'>
                        {values.map((value, index) => (
                            <Reveal as='li' key={value.title} delay={(index % 3) * 90} className='surface rounded-[28px] p-7 sm:p-8'>
                                <span className='grid size-10 place-items-center rounded-full bg-cream font-mono text-[12px] text-ultramarine'>
                                    {`0${index + 1}`}
                                </span>

                                <h3 className='mt-6 font-heading text-xl font-semibold tracking-[-0.025em] text-ink'>
                                    {value.title}
                                </h3>

                                <p className='mt-2.5 text-[15px] leading-relaxed text-ink-soft'>
                                    {value.text}
                                </p>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <section id='team' aria-labelledby='team-title' className='section-y'>
                <div className='shell'>
                    <SectionHeading
                        id='team-title'
                        eyebrow="Team"
                        title="The people"
                        accent="on the other end."
                        intro="A few of the 64 people who scope, build and run Quayside flows. When you message us, one of them answers."
                    />

                    <ul className='mt-12 grid grid-cols-2 gap-4 lg:mt-16 lg:grid-cols-4'>
                        {team.map((member, index) => (
                            <Reveal as='li' key={member.name} delay={(index % 4) * 80}>
                                <article className='surface card-warm h-full overflow-hidden rounded-[24px] border border-transparent'>
                                    <div aria-hidden='true' className='relative grid aspect-[4/3] place-items-center' style={{ background: member.tint }}>
                                        <span className='headline text-[clamp(2.4rem,5vw,3.6rem)] text-ink/75'>
                                            {initials(member.name)}
                                        </span>

                                        <span className='eyebrow absolute bottom-3 left-4 text-ink/45'>
                                            {member.location}
                                        </span>
                                    </div>

                                    <div className='p-4 sm:p-5'>
                                        <h3 className='font-heading text-[16px] font-semibold tracking-[-0.02em] text-ink sm:text-[17px]'>
                                            {member.name}
                                        </h3>

                                        <p className='mt-1 text-[13px] text-stone sm:text-[14px]'>
                                            {member.role}
                                        </p>
                                    </div>
                                </article>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <StatBand />

            <section id='partners' aria-labelledby='partners-title' className='section-y'>
                <div className='shell'>
                    <SectionHeading
                        id='partners-title'
                        eyebrow="Partners"
                        title="Partner"
                        accent="with Quayside."
                        intro="Carriers, brokers and software vendors join the network once and reach every customer we serve."
                        action={(
                            <ButtonLink href='/contact?topic=partner'>
                                Become a partner
                                <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/btn:translate-x-0.5' />
                            </ButtonLink>
                        )}
                    />

                    <ul className='mt-12 grid gap-4 md:grid-cols-3 lg:mt-16'>
                        {partnerTypes.map((partner, index) => (
                            <Reveal as='li' key={partner.title} delay={index * 90} className='surface rounded-[28px] p-7'>
                                <span className='font-mono text-[12px] text-ultramarine'>
                                    {`0${index + 1}`}
                                </span>

                                <h3 className='mt-6 font-heading text-xl font-semibold tracking-[-0.025em] text-ink'>
                                    {partner.title}
                                </h3>

                                <p className='mt-2.5 text-[15px] leading-relaxed text-ink-soft'>
                                    {partner.text}
                                </p>
                            </Reveal>
                        ))}
                    </ul>
                </div>
            </section>

            <CtaPanel
                secondary={{ label: "Read the journal", href: '/journal' }}
            />
        </Layout>
    )
}

export default Company
